// Stripe config for the shared ReconWorkspace (the full card story — kiosk / donate / web / mobile tap /
// event terminal, multi-account ICCT + Kotilinga). Reads the SAME existing /api/manage/stripe-reconcile*
// endpoints the old page uses; nothing backend changes. Account selection is tabs (Stripe's established
// UX). Stripe has no recurring Subscriptions here, so it uses an Origin column instead.
import React from 'react';
import { Box, Typography, Chip, Link, Tooltip, Button } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { get, post } from '../../utils/api';
import { customers } from '../../services/kioskApi';
import { fmtMoney, fmtDate, ACCOUNT_LABEL, accountColor } from '../../components/recon/reconShared';

const bucketOf = (s) => ((s === 'matched' || s === 'needs_link') ? 'order' : s === 'unbooked' ? 'no_order' : s === 'external' ? 'external' : s);

const MATCH_LABEL = {
  tx_id: 'Stripe ID on order', manual_link: 'Stripe ID (linked)', pending: 'Order queued',
  woo_order_id: 'Woo order ID', merchant_ref: 'Merchant ref', donor_amount: 'Donor email + amount',
  donor_desc: 'Donor name (desc)', amount_date: 'Amount + date', mobile_reader: 'Mobile reader',
  event_terminal: 'Event terminal', refunded: 'Refunded',
};
const matchLabelOf = (r) => MATCH_LABEL[r.match_method] || (r.status === 'unbooked' ? 'No order found' : '—');
const isFuzzyMatch = (m) => m === 'donor_amount' || m === 'donor_desc' || m === 'amount_date';
const showMatchHint = (r) => isFuzzyMatch(r.match_method) || r.match_method === 'mobile_reader' || r.match_method === 'event_terminal';

const ORIGIN_OPTIONS = [
  { value: 'all', label: 'All origins' }, { value: 'kiosk', label: 'Kiosk' }, { value: 'donate', label: 'Donate' },
  { value: 'web', label: 'Web' }, { value: 'mobile', label: 'Mobile tap' }, { value: 'terminal', label: 'Event terminal' },
  { value: 'no_marker', label: 'No marker' },
];
const originColor = (o) => ({ kiosk: 'secondary', donate: 'info', web: 'info', mobile: 'warning', terminal: 'default' }[o] || 'default');
const ORIGIN_LABEL = { kiosk: 'kiosk', donate: 'donate', web: 'web', mobile: 'mobile tap', terminal: 'event terminal', no_marker: 'no marker' };

const link = ({ row, orderId }) => post('/api/manage/stripe-reconcile/link', { payment_intent_id: row.payment_intent_id, order_id: orderId });

// Create / book an order for an unbooked (or external "book anyway") row: open Post Payment prefilled.
async function createOrder(r, ctx) {
  let user = null;
  if (r.donor_email) {
    try { const list = await customers.searchUsers(r.donor_email); if (Array.isArray(list) && list.length === 1) user = list[0]; } catch { /* staff searches manually */ }
  }
  ctx.navigate('/post-payment', {
    state: {
      user: user || undefined,
      reconPrefill: { paymentIntentId: r.payment_intent_id, amount: Number(r.amount_num), email: r.donor_email || '', name: r.donor_name || '', description: r.description || '' },
    },
  });
}

const peekCtxOf = (r) => ({ orderId: r.matched_order_id, orderNumber: r.matched_order_number, paymentIntentId: r.payment_intent_id, row: r, canLink: r.status === 'needs_link' });

const stripeConfig = {
  key: 'stripe',
  provider: 'stripe',
  title: 'Reconciliation',   // provider logo now carries the "Stripe" identity (was "Stripe Reconciliation")
  filters: { period: true, account: true, search: false },
  accountAllTab: false,                       // Stripe shows one account at a time (tabs), no "All"
  defaultAccount: 'icct',
  fallbackAccounts: [{ key: 'icct', name: 'ICCT' }],
  extraFilters: [{ key: 'origin', default: 'all', minWidth: 150, options: ORIGIN_OPTIONS, match: (r, v) => r.origin === v }],
  perPage: 25,
  runTooltip: 'Reconcile this account now — scans recent Stripe payments and re-checks open items against orders',

  list: ({ year, month, account }) => get('/api/manage/stripe-reconcile', { year, month, account }),
  run: async ({ account }) => {
    const res = await post('/api/manage/stripe-reconcile/run', { account });
    return `Reconciled ${account}: ${res?.checked ?? 0} payment(s) scanned in the last ${res?.since_days ?? ''} days`;
  },
  rowKey: (r) => r.payment_intent_id,
  bucketOf,

  buckets: [
    { key: 'no_order', label: 'No order' },
    { key: 'order', label: 'Order' },
    { key: 'refunded', label: 'Refunded' },
    { key: 'disputed', label: 'Chargeback' },
    { key: 'external', label: 'External' },
    { key: 'all', label: 'All' },
  ],
  tabNote: (tab, extra) => (
    (extra?.origin === 'mobile' || extra?.origin === 'terminal')
      ? 'In-person reader payments taken outside the portal, identified by the Stripe device (hover the origin chip for the device & location). These aren’t expected to have a portal order and don’t count toward "at risk"; book one only if it should have been an order.'
      : tab === 'order'
        ? 'These have an order. The Match column flags only rows matched by donor/amount/description that don’t yet carry the Stripe id — verify (preview) then Link.'
        : null
  ),

  statTiles: (data) => {
    const c = data?.counts || {}; const a = data?.amounts || {};
    return [
      { label: 'Stripe payments', value: data?.total ?? '—', sub: 'in this period · click for all', color: null, tabKey: 'all' },
      { label: 'Order', value: (c.matched ?? 0) + (c.needs_link ?? 0), sub: 'has an order', color: 'success', tabKey: 'order' },
      { label: 'No order · at risk', value: c.unbooked ?? 0, sub: fmtMoney(a.unbooked), color: 'error', tabKey: 'no_order' },
      { label: 'Refunded', value: c.refunded ?? 0, sub: fmtMoney(a.refunded), color: 'info', tabKey: 'refunded' },
      { label: 'Chargebacks', value: c.disputed ?? 0, sub: fmtMoney(a.disputed), color: 'error', tabKey: 'disputed' },
      { label: 'External (no order expected)', value: c.external ?? 0, sub: 'mobile / terminal', color: 'warning', tabKey: 'external' },
    ];
  },

  link,
  orderPeek: {
    link: (peekCtx) => link({ row: peekCtx.row, orderId: peekCtx.orderId }),
    idMetaKeys: ['tx_id'],
    idLabel: 'Stripe id on order',
  },

  columns: [
    { key: 'date', header: 'Date', width: '10%', render: (r) => <Box sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</Box> },
    {
      key: 'amount', header: 'Amount', width: '9%', align: 'right',
      render: (r) => <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: r.status === 'unbooked' ? 'error.main' : 'inherit' }}>{fmtMoney(r.amount_num)}</Box>,
    },
    {
      key: 'donor', header: 'Donor', width: '22%',
      render: (r) => (
        <>
          {r.donor_name && <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.donor_name}</Typography>}
          {r.donor_email && <Typography variant={r.donor_name ? 'caption' : 'body2'} sx={{ display: 'block', color: r.donor_name ? 'text.secondary' : 'text.primary', wordBreak: 'break-all' }}>{r.donor_email}</Typography>}
          {!r.donor_name && !r.donor_email && (
            r.description
              ? <Tooltip title="Stripe payment description (no donor captured)"><Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>{r.description}</Typography></Tooltip>
              : <Typography variant="caption" sx={{ color: 'text.secondary' }}>no donor captured</Typography>
          )}
        </>
      ),
    },
    {
      key: 'payment', header: 'Stripe payment', width: '15%',
      render: (r) => (
        <Link href={`https://dashboard.stripe.com/payments/${r.payment_intent_id}`} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
          {r.payment_intent_id.slice(0, 16)}…<OpenInNewIcon sx={{ fontSize: 11, ml: 0.3, verticalAlign: 'middle' }} />
        </Link>
      ),
    },
    {
      key: 'origin', header: 'Origin', width: '10%',
      render: (r, ctx) => (
        <>
          <Tooltip title={r.reader_label || r.location_name ? `${r.reader_label || 'reader'}${r.location_name ? ` · ${r.location_name}` : ''}${r.device_type ? ` (${r.device_type})` : ''}` : ''} disableHoverListener={!r.reader_label && !r.location_name}>
            <Chip size="small" label={ORIGIN_LABEL[r.origin] || r.origin} color={originColor(r.origin)} variant={(r.origin === 'no_marker' || r.origin === 'terminal') ? 'outlined' : 'filled'} />
          </Tooltip>
          {ctx.account === 'all' && Array.isArray(ctx.data?.accounts) && ctx.data.accounts.length > 1 && (
            <Chip size="small" variant="outlined" color={accountColor(r.account)} sx={{ ml: 0.5 }} label={(ctx.data.accounts.find((a) => a.key === r.account)?.name) || ACCOUNT_LABEL[r.account] || r.account} />
          )}
        </>
      ),
    },
    {
      key: 'order', header: 'Order?', width: '15%',
      render: (r, ctx) => {
        if (bucketOf(r.status) === 'order') {
          return (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Chip size="small" color={r.status === 'matched' ? 'success' : 'warning'} variant={r.status === 'matched' ? 'outlined' : 'filled'}
                label={`Order ${r.matched_order_number || ''}`.trim()}
                onClick={r.matched_order_id ? () => ctx.openOrderPeek(peekCtxOf(r)) : undefined}
                sx={r.matched_order_id ? { cursor: 'pointer' } : undefined} />
              {r.donor_mismatch && (
                <Tooltip title={`Payment donor (${r.donor_email || '—'}) differs from the order donor (${r.order_donor_name || '—'} · ${r.order_billing_email || '—'}). Could be a household/typo — or the wrong order. Open to check.`}>
                  <Chip size="small" color="warning" label="donor differs" />
                </Tooltip>
              )}
            </Box>
          );
        }
        if (r.status === 'refunded') return <Chip size="small" color="info" variant="outlined" label="Refunded" />;
        if (r.status === 'disputed') return <Chip size="small" color="error" variant="filled" label="Chargeback" />;
        return <Chip size="small" color={r.status === 'external' ? 'default' : 'error'} variant={r.status === 'external' ? 'outlined' : 'filled'} label="No order" />;
      },
    },
    {
      key: 'match', header: 'Match', width: '10%',
      render: (r) => (showMatchHint(r)
        ? <Typography variant="caption" sx={{ color: isFuzzyMatch(r.match_method) ? 'warning.dark' : 'text.secondary' }}>{matchLabelOf(r)}</Typography>
        : null),
    },
    {
      key: 'actions', header: 'Action', width: '10%', align: 'right',
      render: (r, ctx) => (
        <Box sx={{ whiteSpace: 'nowrap' }}>
          {r.status === 'unbooked' && <Button size="small" variant="contained" onClick={() => createOrder(r, ctx)}>Create order</Button>}
          {r.status === 'external' && (
            <Tooltip title="Taken outside the portal (mobile/terminal) — not expected to have an order. Book only if it should have been one.">
              <Button size="small" variant="text" color="inherit" sx={{ color: 'text.secondary' }} onClick={() => createOrder(r, ctx)}>Book anyway</Button>
            </Tooltip>
          )}
          {r.status === 'needs_link' && <Button size="small" variant="contained" onClick={() => ctx.linkRow(r)}>Link</Button>}
        </Box>
      ),
    },
  ],
};

export default stripeConfig;
